import React, { useState, useEffect } from 'react';
import { MemberModel, MemberSubView, SiteSettingModel, BranchModel, ScheduleEvent, StudentOMRModel, StudentExamModel, ScheduleMarker, CompanySettingModel, CompanyName } from '../../../models';
import { auth, db } from '../../../firebase';
import { collection, query, where, onSnapshot, doc, updateDoc, writeBatch, setDoc, deleteDoc, getDoc } from 'firebase/firestore';

// Dedicated Student Views
import StudentProfile from './studentProfile';
import ScheduleMain from './schedule/ScheduleMain';
import StudentStatistics from './statistics/studentStatistics';
import StudentExamRoot from './exam/studentExamRoot';
import StudentExamForm from './exam/studentExamForm';
import StudentQA from './qa/studentQA';
import StudentQAForm from './qa/studentQAForm';
import StudentCounsel from './counsel/studentCounsel';
import StudentCounselForm from './counsel/studentCounselForm';
import OMRHistory from './omr/OMRHistory';
import OMRDetailView from './omr/OMRDetailView';
import StudentMenual from './menual/studentMenual';
import StudentNotice from './notice/StudentNotice';
import ScheduleBookDetail from './schedule/scheduleDetail/ScheduleBookDetail';
import VideoView from './schedule/scheduleDetail/VideoView';
import { ScheduleManager } from './schedule/ScheduleManager';

// Icons
import {
    LogOut, Home, Bell, User, Calendar, BarChart3, Trophy, MessageSquare, ClipboardList,
    Menu, X, Search, Settings, ChevronRight, ChevronLeft, Calculator, BookOpen
} from 'lucide-react';

interface StudentRootProps {
    member: MemberModel;
    siteSettings: SiteSettingModel;
    branches: BranchModel[];
    isReadOnly?: boolean;
    onGoHome?: () => void;
}

const StudentRoot: React.FC<StudentRootProps> = ({ member: initialMember, siteSettings, branches, isReadOnly, onGoHome }) => {
    console.log('Rendering StudentRoot', initialMember?.id);
    const [activeView, setActiveView] = useState<MemberSubView>('profile');
    const [member, setMember] = useState<MemberModel>(initialMember);
    const [schedules, setSchedules] = useState<any[]>([]);
    const [scheduleMarkers, setScheduleMarkers] = useState<ScheduleMarker[]>([]);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<ScheduleEvent | null>(null);
    const [selectedResult, setSelectedResult] = useState<StudentOMRModel | null>(null);
    const [selectedVideo, setSelectedVideo] = useState<{ url: string, title: string } | null>(null);
    const [selectedExam, setSelectedExam] = useState<StudentExamModel | null>(null);
    const [rangeSelection, setRangeSelection] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });
    const [unreadNoticeCount, setUnreadNoticeCount] = useState(0);
    const [companyInfo, setCompanyInfo] = useState<CompanySettingModel | null>(null);

    useEffect(() => {
        // Fetch Company Info
        const fetchCompanyInfo = async () => {
            const docRef = doc(db, 'settings', 'company');
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setCompanyInfo(docSnap.data() as CompanySettingModel);
            }
        };
        fetchCompanyInfo();
        // Sync member profile
        const unsubscribeMember = onSnapshot(doc(db, 'members', initialMember.id), (snap) => {
            if (snap.exists()) setMember({ id: snap.id, ...snap.data() } as MemberModel);
        });

        // Sync schedules
        const q = query(
            collection(db, 'studentSchedules'),
            where('memberId', '==', initialMember.id)
        );
        const unsubscribeSchedules = onSnapshot(q, (snap) => {
            const list: any[] = [];
            snap.forEach(d => list.push({ id: d.id, ...d.data() }));
            setSchedules(list);
        });

        // Sync markers
        const qMarkers = query(
            collection(db, 'schedule_markers'),
            where('memberId', '==', initialMember.id)
        );
        const unsubscribeMarkers = onSnapshot(qMarkers, (snap) => {
            const list: ScheduleMarker[] = [];
            snap.forEach(d => list.push({ id: d.id, ...d.data() } as ScheduleMarker));
            setScheduleMarkers(list);
        });

        // Sync read notices from subcollection
        const readNoticesRef = collection(db, 'members', initialMember.id, 'read_notices');
        const unsubscribeReadNotices = onSnapshot(readNoticesRef, (readSnap) => {
            const readIds = new Set<string>();
            readSnap.forEach(doc => readIds.add(doc.id));

            // Sync notices for unread count
            const qNotices = query(collection(db, 'notices'));
            // Note: This nested listener might not be ideal but for now it's fine. 
            // Better to lift notices state or separate effects but let's stick to the current structure for minimal disruption.
            // Actually, we can just update the count here if we have notices, but we don't have notices in this scope efficiently without fetching.
            // Let's keep the notices listener separate but allow it to access current readIds? 
            // Problem: notices listener is defined below. 

            // Let's restructure slightly: 
            // 1. Listen to notices. 
            // 2. Listen to read_notices.
            // 3. Combine.
        });

        // Sync notices
        const qNotices = query(collection(db, 'notices'));
        const unsubscribeNotices = onSnapshot(qNotices, (snap) => {
            // Store notices snapshot data temporarily or just recount?
            // To properly recount when EITHER changes, we need state for both or a combined effect.
            // Let's assume we just want to update unread count.
        });

        // Let's use a simpler approach: multiple effects or state.
        // I will implement state for readNoticeIds locally in this component.
        return () => {
            unsubscribeMember();
            unsubscribeSchedules();
            unsubscribeMarkers();
            unsubscribeReadNotices();
        };
    }, [initialMember.id]);

    // Separate effect for unread count
    const [readNoticeIds, setReadNoticeIds] = useState<Set<string>>(new Set());
    const [allNotices, setAllNotices] = useState<any[]>([]);
    const [unreadCounselCount, setUnreadCounselCount] = useState(0);
    const [unreadNoticeRawCount, setUnreadNoticeRawCount] = useState(0);
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
        return localStorage.getItem('student-sidebar-collapsed') === 'true';
    });

    useEffect(() => {
        const readNoticesRef = collection(db, 'members', initialMember.id, 'read_notices');
        const unsubRead = onSnapshot(readNoticesRef, (snap) => {
            const ids = new Set<string>();
            snap.forEach(d => ids.add(d.id));
            setReadNoticeIds(ids);
        });

        const qNotices = query(collection(db, 'notices'));
        const unsubNotices = onSnapshot(qNotices, (snap) => {
            const list: any[] = [];
            snap.forEach(d => list.push({ id: d.id, ...d.data() }));
            setAllNotices(list);
        });

        // Listen for unread counsel replies
        const qCounsel = query(
            collection(db, 'studentCounsel'),
            where('memberId', '==', initialMember.id),
            where('isStudentRead', '==', false) // Only fetch unread
        );

        const unsubCounsel = onSnapshot(qCounsel, (snap) => {
            setUnreadCounselCount(snap.size);
            // Note: We only check size. We assume if it has isStudentRead == false, it means there is a reply 
            // because default is undefined (which won't match false) or true. 
            // Actually if we just migrated, existing docs undefined. 
            // Admin sets to false explicitly. So this query is safe.
        });

        return () => {
            unsubRead();
            unsubNotices();
            unsubCounsel();
        };
    }, [initialMember.id]);

    useEffect(() => {
        let count = 0;
        allNotices.forEach(notice => {
            const isForStudent = notice.targetAudience.includes('all') || notice.targetAudience.includes('student');
            const matchesBranch = !notice.branchId || notice.branchId === 'all' || notice.branchId === initialMember.branchId;

            if (isForStudent && matchesBranch && !readNoticeIds.has(notice.id)) {
                count++;
            }
        });
        setUnreadNoticeRawCount(count);
        setUnreadNoticeCount(count + unreadCounselCount);
    }, [allNotices, readNoticeIds, unreadCounselCount]);

    const menuItems: { id: MemberSubView; label: string; icon: any; color: string }[] = [
        { id: 'profile', label: '프로필', icon: User, color: 'text-slate-600' },
        { id: 'schedule', label: '학습일정', icon: Calendar, color: 'text-indigo-600' },
        { id: 'statistics', label: '학습통계', icon: BarChart3, color: 'text-emerald-600' },
        { id: 'exam', label: '성적기록', icon: Trophy, color: 'text-purple-600' },
        { id: 'omr', label: 'OMR 채점', icon: Calculator, color: 'text-emerald-500' },
        { id: 'qa', label: '질문답변', icon: MessageSquare, color: 'text-amber-600' },
        { id: 'counsel', label: '상담기록', icon: ClipboardList, color: 'text-rose-600' },
        { id: 'manual', label: '매뉴얼', icon: BookOpen, color: 'text-indigo-500' },
        { id: 'notice', label: '공지사항', icon: Bell, color: 'text-indigo-600' },
    ];

    // Filter menu items for ReadOnly mode (e.g., hide QA, Manual if not relevant, but let's keep them read-only)
    // For specific views like 'manual' or 'notice', they are naturally read-only usually, so fine.


    const handleUpdateProfile = async (updated: MemberModel) => {
        if (isReadOnly) return;
        try {
            const memberRef = doc(db, 'members', member.id);
            const { id, ...data } = updated;
            await updateDoc(memberRef, data as any);
            alert('프로필이 성공적으로 수정되었습니다.');
        } catch (error) {
            console.error('Error updating profile:', error);
            alert('프로필 수정 중 오류가 발생했습니다.');
        }
    };

    const handleUpdateSchedule = async (id: string, updates: Partial<ScheduleEvent>) => {
        if (isReadOnly) return;
        try {
            const scheduleRef = doc(db, 'studentSchedules', id);
            await updateDoc(scheduleRef, updates as any);
        } catch (error) {
            console.error('Error updating schedule:', error);
            alert('일정 업데이트 중 오류가 발생했습니다.');
        }
    };

    const handleSaveSchedules = async (events: ScheduleEvent[]) => {
        try {
            const batch = writeBatch(db);
            events.forEach(ev => {
                const newDocRef = doc(collection(db, 'studentSchedules'));
                const { id, ...data } = ev;
                batch.set(newDocRef, data);
            });
            await batch.commit();
            alert(`${events.length}개의 일정이 성공적으로 등록되었습니다.`);
        } catch (error) {
            console.error('Error saving schedules:', error);
            alert('일정 저장 중 오류가 발생했습니다.');
        }
    };

    const handleDeleteSchedules = async (ids: string[]) => {
        try {
            const batch = writeBatch(db);
            ids.forEach(id => {
                batch.update(doc(db, 'studentSchedules', id), { isDeleted: true, deletedAt: Date.now() });
            });
            await batch.commit();
            alert(`${ids.length}개의 일정이 휴지통으로 이동되었습니다.`);
        } catch (error) {
            console.error('Error deleting schedules:', error);
            alert('삭제 중 오류가 발생했습니다.');
        }
    };

    const handleRestoreSchedules = async (ids: string[]) => {
        try {
            const batch = writeBatch(db);
            ids.forEach(id => {
                batch.update(doc(db, 'studentSchedules', id), { isDeleted: false, deletedAt: null });
            });
            await batch.commit();
            alert(`${ids.length}개의 일정이 복구되었습니다.`);
        } catch (error) {
            console.error('Error restoring schedules:', error);
            alert('복구 중 오류가 발생했습니다.');
        }
    };

    const handleSaveMarker = async (marker: ScheduleMarker) => {
        try {
            const isNew = !marker.id;
            const ref = isNew ? doc(collection(db, 'schedule_markers')) : doc(db, 'schedule_markers', marker.id);
            const data = { ...marker };
            if (isNew) delete (data as any).id;

            if (isNew) await setDoc(ref, data);
            else await updateDoc(ref, data as any);

            alert(isNew ? '일정이 등록되었습니다.' : '일정이 수정되었습니다.');
        } catch (error) {
            console.error('Error saving marker:', error);
            alert('저장 중 오류가 발생했습니다.');
        }
    };

    const handleDeleteMarker = async (id: string) => {
        if (!confirm('정말 삭제하시겠습니까?')) return;
        try {
            await deleteDoc(doc(db, 'schedule_markers', id));
            alert('삭제되었습니다.');
        } catch (error) {
            console.error('Error deleting marker:', error);
            alert('삭제 중 오류가 발생했습니다.');
        }
    };

    const handlePermanentDeleteSchedules = async (ids: string[]) => {
        if (!confirm('정말 영구 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return;
        try {
            const batch = writeBatch(db);
            ids.forEach(id => {
                batch.delete(doc(db, 'studentSchedules', id));
            });
            await batch.commit();
            alert(`${ids.length}개의 일정이 영구 삭제되었습니다.`);
        } catch (error) {
            console.error('Error permanently deleting schedules:', error);
            alert('영구 삭제 중 오류가 발생했습니다.');
        }
    };

    const renderView = () => {
        const commonProps = { member, siteSettings, branches };
        const activeSchedules = schedules.filter(s => !s.isDeleted);
        const deletedSchedules = schedules.filter(s => s.isDeleted).sort((a, b) => (b.deletedAt || 0) - (a.deletedAt || 0));

        switch (activeView) {
            case 'profile': return <StudentProfile {...commonProps} onUpdate={handleUpdateProfile} isReadOnly={isReadOnly} />;
            case 'schedule':
                return <ScheduleMain
                    member={member}
                    siteSettings={siteSettings}
                    schedules={activeSchedules}
                    recycleBinSchedules={deletedSchedules}
                    scheduleMarkers={scheduleMarkers}
                    rangeSelection={rangeSelection}
                    onRangeSelect={setRangeSelection}
                    onUpdateSchedule={handleUpdateSchedule}
                    onSaveSchedules={handleSaveSchedules}
                    onDeleteSchedules={handleDeleteSchedules}
                    onRestoreSchedules={handleRestoreSchedules}
                    onPermanentDeleteSchedules={handlePermanentDeleteSchedules}
                    onSaveMarker={handleSaveMarker}
                    onDeleteMarker={handleDeleteMarker}
                    onViewDetail={(ev) => {
                        setSelectedEvent(ev);
                        setActiveView('scheduleDetail');
                    }}
                    onOpenManager={() => setActiveView('scheduleManager')}
                    isReadOnly={isReadOnly}
                />;
            case 'scheduleManager':
                const toLocalISOString = (date: Date) => {
                    const offset = date.getTimezoneOffset() * 60000;
                    return new Date(date.getTime() - offset).toISOString().split('T')[0];
                };

                return <ScheduleManager
                    member={member}
                    siteSettings={siteSettings}
                    activeSchedules={activeSchedules}
                    recycleBinSchedules={deletedSchedules}
                    scheduleMarkers={scheduleMarkers}
                    initialStartDate={rangeSelection.start ? toLocalISOString(rangeSelection.start) : undefined}
                    initialEndDate={rangeSelection.end ? toLocalISOString(rangeSelection.end) : undefined}
                    onClose={() => setActiveView('schedule')}
                    onSave={(events) => handleSaveSchedules(events)}
                    onDelete={handleDeleteSchedules}
                    onRestore={handleRestoreSchedules}
                    onPermanentDelete={handlePermanentDeleteSchedules}
                    onSaveMarker={handleSaveMarker}
                    onDeleteMarker={handleDeleteMarker}
                />;
            case 'statistics': return <StudentStatistics member={member} schedules={schedules} />;
            case 'exam': return <StudentExamRoot
                member={member}
                siteSettings={siteSettings}
                onAdd={() => setActiveView('examForm')}
                onEdit={(exam) => { setSelectedExam(exam); setActiveView('examForm'); }}
            />;
            case 'examForm': return <StudentExamForm
                member={member}
                siteSettings={siteSettings}
                exam={selectedExam || undefined}
                onBack={() => setActiveView('exam')}
                onSave={() => setActiveView('exam')}
            />;
            case 'omr': return <OMRHistory
                member={member}
                onViewDetail={(res) => {
                    setSelectedResult(res);
                    setActiveView('omrDetail');
                }}
            />;
            case 'qa': return <StudentQA
                member={member}
                onAdd={() => setActiveView('qaForm')}
                isReadOnly={isReadOnly}
            />;
            case 'qaForm':
                if (isReadOnly) return null;
                return <StudentQAForm
                    member={member}
                    onBack={() => setActiveView('qa')}
                    onSave={() => setActiveView('qa')}
                />;
            case 'counsel': return <StudentCounsel
                member={member}
                onAdd={() => setActiveView('counselForm')}
            />;
            case 'counselForm': return <StudentCounselForm
                member={member}
                onBack={() => setActiveView('counsel')}
                onSave={() => setActiveView('counsel')}
            />;
            case 'manual': return <StudentMenual member={member} />;
            case 'notice': return <StudentNotice member={member} />;
            case 'scheduleDetail': {
                const activeEvent = selectedEvent ? schedules.find(s => s.id === selectedEvent.id) || selectedEvent : null;
                return activeEvent ? (
                    <ScheduleBookDetail
                        event={activeEvent}
                        onBack={() => setActiveView('schedule')}
                        onUpdateStatus={(status) => handleUpdateSchedule(activeEvent.id, { status })}
                        onPlayVideo={(url, title) => {
                            setSelectedVideo({ url, title });
                            setActiveView('videoPlayer');
                        }}
                    />
                ) : null;
            }
            case 'videoPlayer': return selectedVideo ? (
                <VideoView
                    url={selectedVideo.url}
                    title={selectedVideo.title}
                    onBack={() => setActiveView('scheduleDetail')}
                />
            ) : null;
            case 'omrDetail': return selectedResult ? (
                <OMRDetailView
                    result={selectedResult}
                    onBack={() => setActiveView('omr')}
                />
            ) : null;
            default: return null;
        }
    };

    return (
        <div className="flex h-screen bg-slate-50 overflow-hidden font-sans">
            {/* Desktop Sidebar (Sidebar hidden on Mobile) */}
            <aside className={`hidden lg:flex h-full flex-col bg-white border-r border-slate-100 shadow-xl shadow-slate-200/20 z-30 transition-all duration-300 ${isSidebarCollapsed ? 'w-20' : 'w-72'}`}>
                {/* Logo and toggle */}
                <div className={`h-20 flex items-center border-b border-slate-50 ${isSidebarCollapsed ? 'justify-center px-4' : 'px-8 justify-between'}`}>
                    {!isSidebarCollapsed ? (
                        <div className="flex items-center min-w-0">
                            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center overflow-hidden mr-3 shrink-0">
                                <img src={companyInfo?.logoUrl || '/logo.png'} alt="Logo" className="w-full h-full object-contain p-0.5" />
                            </div>
                            <span className="text-lg font-black text-slate-800 tracking-tight truncate">{companyInfo?.companyName || CompanyName}</span>
                        </div>
                    ) : (
                        <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center overflow-hidden shrink-0" title={companyInfo?.companyName || CompanyName}>
                            <img src={companyInfo?.logoUrl || '/logo.png'} alt="Logo" className="w-full h-full object-contain p-0.5" />
                        </div>
                    )}
                    
                    {!isSidebarCollapsed && (
                        <button
                            onClick={() => {
                                const newState = !isSidebarCollapsed;
                                setIsSidebarCollapsed(newState);
                                localStorage.setItem('student-sidebar-collapsed', String(newState));
                            }}
                            className="p-1.5 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-all"
                            title="메뉴 접기"
                        >
                            <ChevronLeft size={16} />
                        </button>
                    )}
                </div>

                {isSidebarCollapsed && (
                    <div className="py-3 flex justify-center border-b border-slate-50">
                        <button
                            onClick={() => {
                                const newState = !isSidebarCollapsed;
                                setIsSidebarCollapsed(newState);
                                localStorage.setItem('student-sidebar-collapsed', String(newState));
                            }}
                            className="p-1.5 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-all"
                            title="메뉴 펼치기"
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>
                )}

                <div className="flex-1 overflow-y-auto p-4 space-y-1 custom-scrollbar">
                    {menuItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = activeView === item.id;
                        
                        if (isSidebarCollapsed) {
                            return (
                                <button
                                    key={item.id}
                                    onClick={() => setActiveView(item.id)}
                                    className={`w-full flex items-center justify-center p-3.5 rounded-2xl transition-all duration-200 group relative ${isActive
                                        ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100'
                                        : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                                        }`}
                                    title={item.label}
                                >
                                    <Icon size={20} className={isActive ? 'text-white' : item.color} />
                                </button>
                            );
                        }

                        return (
                            <button
                                key={item.id}
                                onClick={() => setActiveView(item.id)}
                                className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl transition-all duration-200 group ${isActive
                                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100 translate-x-1'
                                    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                                    }`}
                            >
                                <Icon size={20} className={isActive ? 'text-white' : item.color} />
                                <span className={`text-sm font-black ${isActive ? 'text-white' : 'text-slate-600'}`}>{item.label}</span>
                                {isActive && <ChevronRight size={14} className="ml-auto opacity-50" />}
                            </button>
                        );
                    })}
                </div>

                <div className="p-4 border-t border-slate-50 space-y-2">
                    {!isReadOnly && (
                        isSidebarCollapsed ? (
                            <button
                                onClick={onGoHome}
                                className="w-full flex items-center justify-center p-3.5 rounded-2xl text-slate-500 font-bold hover:bg-slate-100 transition-all mb-2"
                                title="홈 화면"
                            >
                                <Home size={20} />
                            </button>
                        ) : (
                            <button
                                onClick={onGoHome}
                                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl text-slate-500 font-bold text-sm hover:bg-slate-100 transition-all mb-2"
                            >
                                <Home size={20} />
                                홈 화면
                            </button>
                        )
                    )}

                    {!isReadOnly && (
                        isSidebarCollapsed ? (
                            <button
                                onClick={async () => await auth.signOut()}
                                className="w-full flex items-center justify-center p-3.5 rounded-2xl text-rose-500 font-bold hover:bg-rose-50 transition-all"
                                title="로그아웃"
                            >
                                <LogOut size={20} />
                            </button>
                        ) : (
                            <button
                                onClick={async () => await auth.signOut()}
                                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl text-rose-500 font-bold text-sm hover:bg-rose-50 transition-all"
                            >
                                <LogOut size={20} />
                                로그아웃
                            </button>
                        )
                    )}
                </div>
            </aside>

            {/* Main Area */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">

                {/* Mobile/Tablet Header */}
                {activeView !== 'pdf' && (
                    <header className="h-16 lg:h-20 bg-white border-b border-slate-100 px-6 lg:px-12 flex items-center justify-between sticky top-0 z-20 shadow-sm sm:shadow-none">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setIsMobileMenuOpen(true)}
                                className="lg:hidden w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-100"
                            >
                                <Menu size={20} />
                            </button>
                            <div className="flex flex-col">
                                <h2 className="text-base sm:text-xl font-black text-slate-800 leading-tight">
                                    {menuItems.find(i => i.id === activeView)?.label}
                                </h2>
                                <p className="text-[10px] sm:text-xs font-bold text-indigo-500 uppercase tracking-widest hidden sm:block">Student Portal</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 sm:gap-4">
                            <div className="relative">
                                <button
                                    onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                                    className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 hover:bg-slate-100 transition-all relative"
                                >
                                    <Bell size={18} />
                                    {unreadNoticeCount > 0 && (
                                        <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-rose-500 text-white text-[10px] font-black rounded-full border-2 border-white flex items-center justify-center animate-bounce">
                                            {unreadNoticeCount > 99 ? '99+' : unreadNoticeCount}
                                        </span>
                                    )}
                                </button>
                                {isNotificationOpen && (
                                    <>
                                        <div className="fixed inset-0 z-40" onClick={() => setIsNotificationOpen(false)}></div>
                                        <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-2xl border border-slate-100 shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                                            <div className="p-4 border-b border-slate-50 bg-slate-50/50">
                                                <h3 className="text-sm font-black text-slate-800">알림 센터</h3>
                                            </div>
                                            <div className="p-2 space-y-1">
                                                <button
                                                    onClick={() => {
                                                        setActiveView('notice');
                                                        setIsNotificationOpen(false);
                                                    }}
                                                    className="w-full text-left p-3 rounded-xl hover:bg-slate-50 transition-colors flex items-center gap-3 group"
                                                >
                                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${unreadNoticeRawCount > 0 ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-400'}`}>
                                                        <Bell size={16} />
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className={`text-xs font-bold ${unreadNoticeRawCount > 0 ? 'text-slate-800' : 'text-slate-500'}`}>공지사항</p>
                                                        <p className="text-[10px] text-slate-400">
                                                            {unreadNoticeRawCount > 0 ? `${unreadNoticeRawCount}개의 안 읽은 공지` : '새로운 공지사항 없음'}
                                                        </p>
                                                    </div>
                                                    {unreadNoticeRawCount > 0 && <div className="w-2 h-2 bg-rose-500 rounded-full"></div>}
                                                </button>

                                                <button
                                                    onClick={() => {
                                                        setActiveView('counsel');
                                                        setIsNotificationOpen(false);
                                                    }}
                                                    className="w-full text-left p-3 rounded-xl hover:bg-slate-50 transition-colors flex items-center gap-3 group"
                                                >
                                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${unreadCounselCount > 0 ? 'bg-rose-100 text-rose-500' : 'bg-slate-100 text-slate-400'}`}>
                                                        <ClipboardList size={16} />
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className={`text-xs font-bold ${unreadCounselCount > 0 ? 'text-slate-800' : 'text-slate-500'}`}>상담 답변</p>
                                                        <p className="text-[10px] text-slate-400">
                                                            {unreadCounselCount > 0 ? `${unreadCounselCount}개의 새로운 답변` : '새로운 답변 없음'}
                                                        </p>
                                                    </div>
                                                    {unreadCounselCount > 0 && <div className="w-2 h-2 bg-rose-500 rounded-full"></div>}
                                                </button>
                                            </div>
                                            {unreadNoticeCount === 0 && (
                                                <div className="px-4 py-8 text-center text-slate-300">
                                                    <p className="text-xs font-bold">새로운 알림이 없습니다</p>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="hidden sm:block h-8 w-px bg-slate-100 mx-2"></div>
                            <div className="flex items-center gap-3 pl-1">
                                <div className="text-right hidden sm:block">
                                    <p className="text-xs font-black text-slate-800 leading-tight">{member.name}</p>
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tight">
                                        {member.schoolName} {member.grade}
                                    </p>
                                </div>
                                <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shadow-sm overflow-hidden">
                                    <User size={20} />
                                </div>
                            </div>
                        </div>
                    </header>
                )}

                {/* Content Area */}
                <main className={`flex-1 ${activeView === 'pdf' ? 'overflow-hidden p-0 bg-white' : 'overflow-y-auto p-4 sm:p-8 lg:p-12 custom-scrollbar bg-slate-50'}`}>
                    <div className={activeView === 'pdf' ? 'w-full h-full' : 'max-w-6xl mx-auto'}>
                        {renderView()}
                    </div>
                </main>

                {/* Mobile Bottom Navigation (Visible only on very small screens) */}
                <nav className="sm:hidden h-20 bg-white border-t border-slate-100 px-6 flex items-center justify-between pb-2 shrink-0 z-20">
                    {menuItems.slice(0, 4).map((item) => {
                        const Icon = item.icon;
                        const isActive = activeView === item.id;
                        return (
                            <button
                                key={item.id}
                                onClick={() => setActiveView(item.id)}
                                className={`flex flex-col items-center gap-1 transition-all ${isActive ? 'text-indigo-600 scale-110' : 'text-slate-400'}`}
                            >
                                <Icon size={22} className={isActive ? 'text-indigo-600' : ''} />
                                <span className="text-[9px] font-black uppercase tracking-widest">{item.label}</span>
                            </button>
                        );
                    })}
                    <button
                        onClick={() => setIsMobileMenuOpen(true)}
                        className="flex flex-col items-center gap-1 text-slate-400"
                    >
                        <Menu size={22} />
                        <span className="text-[9px] font-black uppercase tracking-widest">메뉴</span>
                    </button>
                </nav>
            </div>

            {/* Mobile Navigation Overlay */}
            {isMobileMenuOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)}></div>
                    <aside className="absolute top-0 left-0 w-80 h-full bg-white shadow-2xl animate-in slide-in-from-left duration-300 flex flex-col">
                        <div className="h-20 flex items-center justify-between px-8 border-b border-slate-50">
                            <div className="flex items-center">
                                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center overflow-hidden mr-3 shrink-0">
                                    <img src={companyInfo?.logoUrl || '/logo.png'} alt="Logo" className="w-full h-full object-contain p-0.5" />
                                </div>
                                <span className="text-lg font-black text-slate-800">Menu</span>
                            </div>
                            <button onClick={() => setIsMobileMenuOpen(false)} className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-1">
                            {menuItems.map((item) => {
                                const Icon = item.icon;
                                const isActive = activeView === item.id;
                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => { setActiveView(item.id); setIsMobileMenuOpen(false); }}
                                        className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl transition-all ${isActive ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:bg-slate-50'
                                            }`}
                                    >
                                        <Icon size={20} className={isActive ? 'text-white' : item.color} />
                                        <span className={`text-sm font-black ${isActive ? 'text-white' : 'text-slate-600'}`}>{item.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="p-6 border-t border-slate-50 space-y-2">

                            <button
                                onClick={() => { if (onGoHome) onGoHome(); }}
                                className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl bg-slate-50 text-slate-500 font-black text-sm"
                            >
                                <Home size={20} /> 홈 화면
                            </button>
                            <button
                                onClick={async () => await auth.signOut()}
                                className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl bg-rose-50 text-rose-500 font-black text-sm"
                            >
                                <LogOut size={20} /> 로그아웃
                            </button>
                        </div>
                    </aside>
                </div>
            )}
        </div>
    );
};

export default StudentRoot;
